import React, { useState, useEffect, useRef } from 'react';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { addspecialitys, updateAppointmentSpeciality, deleteAppointmentSpeciality, specialitySetdoctor, deleteSpeciality, updateSpeciality, createAppointmentSpeciality, removeSpeciality, checkAppointmentByTypeId, checkAppointmentByVisio } from '../../redux/doctorSlice';
import { getallspecilitylist, getSpCategoryList } from '../../redux/masterSlice';
import { hasControl, SECURED_VIDEO_MSG } from '../../redux/subscriptionSlice';
import empty2 from '../../assets/img/colorkit (2).svg';
import empty3 from '../../assets/img/colorkit (3).svg';
import { Plus, X, Trash2, Edit, Search, ChevronDown, ChevronUp } from 'lucide-react';
import MasterLabel from '../common/MasterLabel';
import SpecialtyIcon from '../common/SpecialtyIcon';
import { useMasterLabel } from '../../hooks/useMasterLabel';
import { settingsPrivacy as empty1 } from '../../assets/img/illustrations';
import { doctorCurrencySymbol } from '../../utils/currency';

// Inline helpers used inside loops + dropdown buttons. Rules-of-hooks forbid calling
// useMasterLabel inside a map callback, so each item lives in its own component.
const PickerOptionRow = ({ option, onClick }) => {
    const label = useMasterLabel('masterSpecialty', option.value || option.specialityId, 'name', option.label);
    return (
        <li
            onClick={onClick}
            className="px-4 py-2.5 transition text-sm cursor-pointer hover:bg-gradient-to-r hover:from-[#4ABDBD] hover:to-[#3AA8A8] hover:text-white border-b border-gray-50 last:border-0 font-medium text-gray-700 flex items-center gap-2"
        >
            <SpecialtyIcon specialtyId={option.value || option.specialityId} categoryId={option.categoryId} size={16} />
            <span className="truncate min-w-0">{label}</span>
        </li>
    );
};

// Renders the currently-selected specialty label inside a dropdown trigger button.
// Used in "Make Active Specialty" + "Add Appointment" pickers — both display the name
// of the currently chosen specialty, which must follow the active language.
const SelectedSpecialtyText = ({ specialtyId, fallback }) => {
    const label = useMasterLabel('masterSpecialty', specialtyId, 'name', fallback);
    return <>{label}</>;
};

// Per-row category label inside the category filter dropdown.
const CategoryRow = ({ cat, onClick }) => {
    const label = useMasterLabel('masterSpecialtyCategory', cat?.spcategoryId, 'spcategoryName', cat?.spcategoryName || '-');
    return (
        <li
            onClick={onClick}
            className="px-4 py-2.5 transition text-sm cursor-pointer hover:bg-gradient-to-r hover:from-[#4ABDBD] hover:to-[#3AA8A8] hover:text-white border-b border-gray-50 last:border-0 font-medium text-gray-700 flex items-center gap-2"
        >
            <SpecialtyIcon categoryId={cat?.spcategoryId} size={16} />
            <span className="truncate min-w-0">{label}</span>
        </li>
    );
};

function SpecialitiesTab() {
    const { t } = useTranslation('specialitiesTab');
    const dispatch = useDispatch();
    const { specialties, selectedDoctor, loading, checkAppointmentLoading, checkAppointmentVisioLoading } = useSelector((state) => state.doctor);
    const { specialties: masterSpecialties, specialtyLoading, spCategoryList, spCategoryListLoading } = useSelector((state) => state.master);
    const doctorId = selectedDoctor?.doctorId || localStorage.getItem('ID');
    const language = localStorage.getItem('i18nextLng') || 'en';
    // Video (visio) is only configurable if this doctor's plan includes secured_video.
    // Fail-open when planControls hasn't loaded yet (== null) so we never wrongly lock.
    const videoAllowed = selectedDoctor?.planControls == null
        ? true
        : hasControl(selectedDoctor.planControls, 'secured_video');
    const doctorSelected = selectedDoctor?.appointmentSpeciality || [];

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isActiveSpecialtyModalOpen, setIsActiveSpecialtyModalOpen] = useState(false);
    const [selectedSpecialty, setSelectedSpecialty] = useState('');
    const [selectedColor, setSelectedColor] = useState('#4ABDBD');
    const [newSpecialty, setNewSpecialty] = useState({ name: '', color: '#4ABDBD' });
    const [editSpecialty, setEditSpecialty] = useState({ name: '', color: '#4ABDBD', specialtyId: null });
    const [isAppointmentModalOpen, setIsAppointmentModalOpen] = useState(false);
    const [appointmentData, setAppointmentData] = useState({
        specialty: '',
        type: '',
        hours: 0,
        minutes: 0,
        price: '',
        visioAccepted: false,
        typeId: null,
    });
    const [isAdding, setIsAdding] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isCreatingAppointment, setIsCreatingAppointment] = useState(false);
    const [appointmentCheckError, setAppointmentCheckError] = useState('');
    const [appointmentCheckVisioError, setAppointmentCheckVisioError] = useState('');
    const [canEditOnlyMode, setCanEditOnlyMode] = useState(false);
    const [modalError, setModalError] = useState('');
    const [errors, setErrors] = useState({
        newSpecialtyName: '',
        editSpecialtyName: '',
        appointmentSpecialty: '',
        appointmentType: '',
        appointmentPrice: '',
        appointmentDuration: '',
    });
    const [charLimitReached, setCharLimitReached] = useState({});

    // Custom dropdown state
    const [selectedCategoryFilter, setSelectedCategoryFilter] = useState(null);
    const [isCategoryFilterDropdownOpen, setIsCategoryFilterDropdownOpen] = useState(false);
    const [categorySearchQuery, setCategorySearchQuery] = useState('');
    const [isSpecialtyDropdownOpen, setIsSpecialtyDropdownOpen] = useState(false);
    const [specialtySearchQuery, setSpecialtySearchQuery] = useState('');
    const [isAppointmentSpecialtyDropdownOpen, setIsAppointmentSpecialtyDropdownOpen] = useState(false);
    const [appointmentSpecialtySearchQuery, setAppointmentSpecialtySearchQuery] = useState('');

    // Refs for clicking outside
    const categoryFilterDropdownRef = useRef(null);
    const categorySearchInputRef = useRef(null);
    const specialtyDropdownRef = useRef(null);
    const appointmentSpecialtyDropdownRef = useRef(null);

    // Filter categories locally by search
    const filteredCategoryList = (spCategoryList || []).filter(cat =>
        !categorySearchQuery.trim() || (cat.spcategoryName || '').toLowerCase().includes(categorySearchQuery.trim().toLowerCase())
    );

    // Define all specialties options
    const specialtiesOptions = specialties.map(item => ({
        value: item.uniqueId,
        label: item.name,
        color: item.color,
        uniqueId: item.uniqueId,
        name: item.name,
    }));

    // Map doctorSelected to initial selected options
    const initialSelectedSpecialties = doctorSelected.map(specialty => {
        const matchingSpecialty = specialties.find(s => s.name === specialty.name && s.color === specialty.color);
        console.log('specialty', specialty);
        if (matchingSpecialty) {
            return {
                value: matchingSpecialty.uniqueId,
                label: matchingSpecialty.name,
                color: matchingSpecialty.color,
                specialtyId: specialty.specialtyId,
                name: matchingSpecialty.name,
            };
        } else {
            return {
                value: specialty.specialtyId,
                label: specialty.name,
                color: specialty.color,
                specialtyId: specialty.specialtyId,
                name: specialty.name,
            };
        }
    });

    const toggleActiveSpecialtyModal = () => {
        setIsActiveSpecialtyModalOpen(!isActiveSpecialtyModalOpen);
        setSelectedSpecialty('');
        setSelectedColor('#4ABDBD');
        setModalError('');
        setSpecialtySearchQuery('');
        setSelectedCategoryFilter(null);
        setIsCategoryFilterDropdownOpen(false);
        setCategorySearchQuery('');
        // Fetch specialties and categories when modal opens
        if (!isActiveSpecialtyModalOpen) {
            dispatch(getallspecilitylist());
            dispatch(getSpCategoryList());
        }
    };

    const handleColorChange = (e) => {
        setSelectedColor(e.target.value);
    };

    const handleSpecialtySearchChange = (e) => {
        setSpecialtySearchQuery(e.target.value);
    };

    const handleSpecialtyItemSelect = (option) => {
        setSelectedSpecialty(option.value);
        setIsSpecialtyDropdownOpen(false);
        setSpecialtySearchQuery('');
    };

    const handleAppointmentSpecialtySearchChange = (e) => {
        setAppointmentSpecialtySearchQuery(e.target.value);
    };

    const handleAppointmentSpecialtyItemSelect = (specialty) => {
        setAppointmentData(prev => ({
            ...prev,
            specialty: specialty.specialtyId,
        }));
        setErrors(prev => ({
            ...prev,
            appointmentSpecialty: '',
        }));
        setIsAppointmentSpecialtyDropdownOpen(false);
        setAppointmentSpecialtySearchQuery('');
    };

    const handleAddClick = () => {
        setIsModalOpen(true);
    };

    const toggleModal = () => {
        setIsModalOpen(!isModalOpen);
        setNewSpecialty({ name: '', color: '#4ABDBD' });
        setModalError('');
        setErrors(prev => ({ ...prev, newSpecialtyName: '' }));
        setCharLimitReached({ ...charLimitReached, newSpecialtyName: false });
    };

    const toggleEditModal = () => {
        setIsEditModalOpen(!isEditModalOpen);
        setEditSpecialty({ name: '', color: '#4ABDBD', specialtyId: null });
        setModalError('');
        setErrors(prev => ({ ...prev, editSpecialtyName: '' }));
        setCharLimitReached({ ...charLimitReached, editSpecialtyName: false });
    };

    const toggleAppointmentModal = () => {
        setIsAppointmentModalOpen(!isAppointmentModalOpen);
        setModalError('');
        setAppointmentCheckError('');
        setAppointmentCheckVisioError('');
        setCanEditOnlyMode(false);
        setIsAppointmentSpecialtyDropdownOpen(false);
        setAppointmentSpecialtySearchQuery('');
        setErrors(prev => ({
            ...prev,
            appointmentSpecialty: '',
            appointmentType: '',
            appointmentPrice: '',
            appointmentDuration: '',
        }));
        setCharLimitReached({ ...charLimitReached, appointmentType: false });
    };

    // Handle category filter selection
    const handleCategoryFilterSelect = (category) => {
        setSelectedCategoryFilter(category);
        setIsCategoryFilterDropdownOpen(false);
        setCategorySearchQuery('');
        // Reset specialty selection when category changes
        setSelectedSpecialty('');
        setSpecialtySearchQuery('');
        // Re-fetch specialties filtered by category
        if (category) {
            dispatch(getallspecilitylist({ categoryId: category.spcategoryId }));
        }
    };

    // Click outside listener
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (categoryFilterDropdownRef.current && !categoryFilterDropdownRef.current.contains(event.target)) {
                setIsCategoryFilterDropdownOpen(false);
                setCategorySearchQuery('');
            }
            if (specialtyDropdownRef.current && !specialtyDropdownRef.current.contains(event.target)) {
                setIsSpecialtyDropdownOpen(false);
            }
            if (appointmentSpecialtyDropdownRef.current && !appointmentSpecialtyDropdownRef.current.contains(event.target)) {
                setIsAppointmentSpecialtyDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Filtered options for Make Active Specialty Modal
    const filteredAvailableOptions = (masterSpecialties || [])
        .filter(spec => {
            const id = spec.specialtyId || spec.specialityId || spec.id;
            const matchesSearch = spec.name.toLowerCase().includes(specialtySearchQuery.toLowerCase());
            return id && !doctorSelected.some(sel => sel.name === spec.name) && matchesSearch;
        })
        .map(spec => {
            const id = spec.specialtyId || spec.specialityId || spec.id;
            return {
                value: String(id),
                label: spec.name,
                name: spec.name,
                specialityId: id,
                categoryId: spec.categoryId
            };
        });

    // Filtered options for Appointment Modal
    const filteredDoctorSelected = (doctorSelected || [])
        .filter(specialty =>
            specialty.name.toLowerCase().includes(appointmentSpecialtySearchQuery.toLowerCase())
        );

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (name === 'name') {
            // Sanitize input - remove dangerous characters
            const sanitized = value.replace(/[<>]/g, '');

            // Enforce max length
            const finalValue = sanitized.length > 20 ? sanitized.substring(0, 20) : sanitized;
            setNewSpecialty(prev => ({ ...prev, [name]: finalValue }));

            // Check if character limit is reached
            if (finalValue.length >= 20) {
                setCharLimitReached({ ...charLimitReached, newSpecialtyName: true });
            } else {
                setCharLimitReached({ ...charLimitReached, newSpecialtyName: false });
            }

            // Validate
            if (!finalValue.trim()) {
                setErrors(prev => ({ ...prev, newSpecialtyName: t('specialitiesTab.errors.newSpecialtyName_required') }));
            } else if (/^\s+$/.test(finalValue)) {
                setErrors(prev => ({ ...prev, newSpecialtyName: t('specialitiesTab.name_only_whitespace') }));
            } else if (finalValue.trim().length < 2) {
                setErrors(prev => ({ ...prev, newSpecialtyName: t('specialitiesTab.name_min_2_chars') }));
            } else if (finalValue.length > 20) {
                setErrors(prev => ({ ...prev, newSpecialtyName: t('specialitiesTab.errors.newSpecialtyName_maxLength') }));
            } else if (specialties.some(s => s.name.toLowerCase() === finalValue.trim().toLowerCase())) {
                setErrors(prev => ({ ...prev, newSpecialtyName: t('specialitiesTab.errors.newSpecialtyName_exists') }));
            } else if (!/^[A-Za-zÀ-ÿ\s]+$/.test(finalValue)) {
                setErrors(prev => ({ ...prev, newSpecialtyName: t('specialitiesTab.errors.newSpecialtyName_alphabetsOnly') }));
            } else {
                setErrors(prev => ({ ...prev, newSpecialtyName: '' }));
            }
        } else {
            setNewSpecialty(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleEditInputChange = (e) => {
        const { name, value } = e.target;

        if (name === 'name') {
            // Sanitize input - remove dangerous characters
            const sanitized = value.replace(/[<>]/g, '');

            // Enforce max length
            const finalValue = sanitized.length > 20 ? sanitized.substring(0, 20) : sanitized;
            setEditSpecialty(prev => ({ ...prev, [name]: finalValue }));

            // Check if character limit is reached
            if (finalValue.length >= 20) {
                setCharLimitReached({ ...charLimitReached, editSpecialtyName: true });
            } else {
                setCharLimitReached({ ...charLimitReached, editSpecialtyName: false });
            }

            // Validate
            if (!finalValue.trim()) {
                setErrors(prev => ({ ...prev, editSpecialtyName: t('specialitiesTab.errors.editSpecialtyName_required') }));
            } else if (/^\s+$/.test(finalValue)) {
                setErrors(prev => ({ ...prev, editSpecialtyName: t('specialitiesTab.name_only_whitespace') }));
            } else if (finalValue.trim().length < 2) {
                setErrors(prev => ({ ...prev, editSpecialtyName: t('specialitiesTab.name_min_2_chars') }));
            } else if (finalValue.length > 20) {
                setErrors(prev => ({ ...prev, editSpecialtyName: t('specialitiesTab.errors.editSpecialtyName_maxLength') }));
            } else if (specialties.some(s => s.name.toLowerCase() === finalValue.trim().toLowerCase() && s.uniqueId !== editSpecialty.specialtyId)) {
                setErrors(prev => ({ ...prev, editSpecialtyName: t('specialitiesTab.errors.editSpecialtyName_exists') }));
            } else if (!/^[A-Za-zÀ-ÿ\s]+$/.test(finalValue)) {
                setErrors(prev => ({ ...prev, editSpecialtyName: t('specialitiesTab.errors.editSpecialtyName_alphabetsOnly') }));
            } else {
                setErrors(prev => ({ ...prev, editSpecialtyName: '' }));
            }
        } else {
            setEditSpecialty(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleAppointmentInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        let newValue = value;

        if (name === 'type') {
            // Sanitize input - remove dangerous characters
            const sanitized = value.replace(/[<>]/g, '');

            // Disallow numbers and special characters (allow only letters and spaces)
            if (sanitized !== '' && !/^[A-Za-zÀ-ÿ\s]*$/.test(sanitized)) return;

            // Enforce max length
            newValue = sanitized.length > 20 ? sanitized.substring(0, 20) : sanitized;

            setAppointmentData(prev => ({
                ...prev,
                [name]: newValue,
            }));

            // Check if character limit is reached
            if (newValue.length >= 20) {
                setCharLimitReached({ ...charLimitReached, appointmentType: true });
            } else {
                setCharLimitReached({ ...charLimitReached, appointmentType: false });
            }

            // Validate
            if (!newValue.trim()) {
                setErrors(prev => ({ ...prev, appointmentType: t('specialitiesTab.errors.appointmentType_required') }));
            } else if (/^\s+$/.test(newValue)) {
                setErrors(prev => ({ ...prev, appointmentType: t('specialitiesTab.appt_type_only_whitespace') }));
            } else if (newValue.trim().length < 2) {
                setErrors(prev => ({ ...prev, appointmentType: t('specialitiesTab.appt_type_min_2_chars') }));
            } else if (newValue.length > 20) {
                setErrors(prev => ({ ...prev, appointmentType: t('specialitiesTab.errors.appointmentType_maxLength') }));
            } else if (!/^[A-Za-zÀ-ÿ\s]+$/.test(newValue)) {
                setErrors(prev => ({ ...prev, appointmentType: t('specialitiesTab.appt_type_letters_spaces_only') }));
            } else if (appointmentData.specialty && doctorSelected.find(s => s.specialtyId === appointmentData.specialty)?.appointmentTypes?.some(t => t.type.toLowerCase() === newValue.trim().toLowerCase() && t.typeId !== appointmentData.typeId)) {
                setErrors(prev => ({ ...prev, appointmentType: t('specialitiesTab.errors.appointmentType_exists') }));
            } else if (appointmentData.specialty && doctorSelected.find(s => s.specialtyId === appointmentData.specialty)?.name.toLowerCase() === newValue.trim().toLowerCase()) {
                setErrors(prev => ({ ...prev, appointmentType: t('specialitiesTab.errors.appointmentType_sameAsSpecialty') }));
            } else {
                setErrors(prev => ({ ...prev, appointmentType: '' }));
            }
        } else {
            if (name === 'price') {
                newValue = value.replace(/[^0-9]/g, ''); // Only allow numbers
                if (newValue.length > 6) {
                    newValue = newValue.slice(0, 6);
                }
            }

            setAppointmentData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : newValue,
            }));

            if (name === 'specialty') {
                setErrors(prev => ({
                    ...prev,
                    appointmentSpecialty: value ? '' : t('specialitiesTab.errors.appointmentSpecialty_required'),
                }));
            } else if (name === 'price') {
                if (!newValue) {
                    setErrors(prev => ({ ...prev, appointmentPrice: t('specialitiesTab.errors.appointmentPrice_required') }));
                } else if (isNaN(newValue) || Number(newValue) <= 0) {
                    setErrors(prev => ({ ...prev, appointmentPrice: t('specialitiesTab.errors.appointmentPrice_invalid') }));
                } else if (Number(newValue) > 999999) {
                    setErrors(prev => ({ ...prev, appointmentPrice: t('specialitiesTab.errors.appointmentPrice_max') }));
                } else {
                    setErrors(prev => ({ ...prev, appointmentPrice: '' }));
                }
            } else if (name === 'hours' || name === 'minutes') {
                const hours = name === 'hours' ? Number(value) : appointmentData.hours;
                const minutes = name === 'minutes' ? Number(value) : appointmentData.minutes;
                const totalMin = hours * 60 + minutes;
                if (totalMin === 0) {
                    setErrors(prev => ({ ...prev, appointmentDuration: t('specialitiesTab.errors.appointmentDuration_required') }));
                } else if (totalMin < 15) {
                    setErrors(prev => ({ ...prev, appointmentDuration: t('specialitiesTab.errors.appointmentDuration_minimum') }));
                } else {
                    setErrors(prev => ({ ...prev, appointmentDuration: '' }));
                }
            }
        };
    };

    const validateNewSpecialty = () => {
        let isValid = true;
        const newErrors = { ...errors };
        if (!newSpecialty.name.trim()) {
            newErrors.newSpecialtyName = t('specialitiesTab.errors.newSpecialtyName_required');
            isValid = false;
        } else if (/^\s+$/.test(newSpecialty.name)) {
            newErrors.newSpecialtyName = t('specialitiesTab.name_only_whitespace');
            isValid = false;
        } else if (newSpecialty.name.trim().length < 2) {
            newErrors.newSpecialtyName = t('specialitiesTab.name_min_2_chars');
            isValid = false;
        } else if (newSpecialty.name.length > 20) {
            newErrors.newSpecialtyName = t('specialitiesTab.errors.newSpecialtyName_maxLength');
            isValid = false;
        } else if (specialties.some(s => s.name.toLowerCase() === newSpecialty.name.trim().toLowerCase())) {
            newErrors.newSpecialtyName = t('specialitiesTab.errors.newSpecialtyName_exists');
            isValid = false;
        } else if (!/^[A-Za-zÀ-ÿ\s]+$/.test(newSpecialty.name)) {
            newErrors.newSpecialtyName = t('specialitiesTab.errors.newSpecialtyName_alphabetsOnly');
            isValid = false;
        } else {
            newErrors.newSpecialtyName = '';
        }
        setErrors(newErrors);
        return isValid;
    };

    const validateEditSpecialty = () => {
        let isValid = true;
        const newErrors = { ...errors };
        if (!editSpecialty.name.trim()) {
            newErrors.editSpecialtyName = t('specialitiesTab.errors.editSpecialtyName_required');
            isValid = false;
        } else if (/^\s+$/.test(editSpecialty.name)) {
            newErrors.editSpecialtyName = t('specialitiesTab.name_only_whitespace');
            isValid = false;
        } else if (editSpecialty.name.trim().length < 2) {
            newErrors.editSpecialtyName = t('specialitiesTab.name_min_2_chars');
            isValid = false;
        } else if (editSpecialty.name.length > 20) {
            newErrors.editSpecialtyName = t('specialitiesTab.errors.editSpecialtyName_maxLength');
            isValid = false;
        } else if (specialties.some(s => s.name.toLowerCase() === editSpecialty.name.trim().toLowerCase() && s.uniqueId !== editSpecialty.specialtyId)) {
            newErrors.editSpecialtyName = t('specialitiesTab.errors.editSpecialtyName_exists');
            isValid = false;
        } else if (!/^[A-Za-zÀ-ÿ\s]+$/.test(editSpecialty.name)) {
            newErrors.editSpecialtyName = t('specialitiesTab.errors.editSpecialtyName_alphabetsOnly');
            isValid = false;
        } else {
            newErrors.editSpecialtyName = '';
        }
        setErrors(newErrors);
        return isValid;
    };

    const validateAppointment = () => {
        let isValid = true;
        const newErrors = { ...errors };
        if (!appointmentData.specialty) {
            newErrors.appointmentSpecialty = t('specialitiesTab.errors.appointmentSpecialty_required');
            isValid = false;
        } else {
            newErrors.appointmentSpecialty = '';
        }
        if (!appointmentData.type.trim()) {
            newErrors.appointmentType = t('specialitiesTab.errors.appointmentType_required');
            isValid = false;
        } else if (/^\s+$/.test(appointmentData.type)) {
            newErrors.appointmentType = t('specialitiesTab.appt_type_only_whitespace');
            isValid = false;
        } else if (appointmentData.type.trim().length < 2) {
            newErrors.appointmentType = t('specialitiesTab.appt_type_min_2_chars');
            isValid = false;
        } else if (appointmentData.type.length > 20) {
            newErrors.appointmentType = t('specialitiesTab.errors.appointmentType_maxLength');
            isValid = false;
        } else if (!/^[A-Za-zÀ-ÿ\s]+$/.test(appointmentData.type)) {
            newErrors.appointmentType = t('specialitiesTab.appt_type_letters_spaces_only');
            isValid = false;
        } else if (doctorSelected.find(s => s.specialtyId === appointmentData.specialty)?.appointmentTypes?.some(t => t.type.toLowerCase() === appointmentData.type.trim().toLowerCase() && t.typeId !== appointmentData.typeId)) {
            newErrors.appointmentType = t('specialitiesTab.errors.appointmentType_exists');
            isValid = false;
        } else if (appointmentData.specialty && doctorSelected.find(s => s.specialtyId === appointmentData.specialty)?.name.toLowerCase() === appointmentData.type.trim().toLowerCase()) {
            newErrors.appointmentType = t('specialitiesTab.errors.appointmentType_sameAsSpecialty');
            isValid = false;
        } else {
            newErrors.appointmentType = '';
        }
        if (!appointmentData.price) {
            newErrors.appointmentPrice = t('specialitiesTab.errors.appointmentPrice_required');
            isValid = false;
        } else if (isNaN(appointmentData.price) || Number(appointmentData.price) <= 0) {
            newErrors.appointmentPrice = t('specialitiesTab.errors.appointmentPrice_invalid');
            isValid = false;
        } else if (Number(appointmentData.price) > 999999) {
            newErrors.appointmentPrice = t('specialitiesTab.errors.appointmentPrice_max');
            isValid = false;
        } else {
            newErrors.appointmentPrice = '';
        }
        const totalMin = appointmentData.hours * 60 + appointmentData.minutes;
        if (totalMin === 0) {
            newErrors.appointmentDuration = t('specialitiesTab.errors.appointmentDuration_required');
            isValid = false;
        } else if (totalMin < 15) {
            newErrors.appointmentDuration = t('specialitiesTab.errors.appointmentDuration_minimum');
            isValid = false;
        } else {
            newErrors.appointmentDuration = '';
        }
        setErrors(newErrors);
        return isValid;
    };

    const handleAddSpecialty = async (e) => {
        e.preventDefault();
        setModalError('');
        if (!validateNewSpecialty()) return;

        const result = await Swal.fire({
            title: t('specialitiesTab.confirm_add_title'),
            text: t('specialitiesTab.confirm_add_text'),
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#d33',
            confirmButtonText: t('specialitiesTab.yes_add'),
            cancelButtonText: t('specialitiesTab.cancel_button'),
        });

        if (!result.isConfirmed) return;

        setIsAdding(true);
        try {
            await dispatch(
                addspecialitys({
                    name: newSpecialty.name,
                    color: newSpecialty.color,
                    doctorId
                })
            ).unwrap();

            toast.success(t('specialitiesTab.success_add_text'));

            toggleModal();
        } catch (err) {
            const errorMessage = err?.error?.response?.data?.message?.[language] || t('specialitiesTab.errors.addSpecialty_failed');
            toast.warn(errorMessage);
            setModalError(errorMessage);
        } finally {
            setIsAdding(false);
        }
    };

    const handleEditSpecialty = async (e) => {
        e.preventDefault();
        setModalError('');
        if (!validateEditSpecialty()) return;

        const result = await Swal.fire({
            title: t('specialitiesTab.confirm_edit_title'),
            text: t('specialitiesTab.confirm_edit_text'),
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#d33',
            confirmButtonText: t('specialitiesTab.yes_edit'),
            cancelButtonText: t('specialitiesTab.cancel_button'),
        });

        if (!result.isConfirmed) return;

        setIsUpdating(true);
        try {
            await dispatch(
                updateSpeciality({
                    specialtyId: editSpecialty.specialtyId,
                    name: editSpecialty.name,
                    color: editSpecialty.color
                })
            ).unwrap();

            toast.success(t('specialitiesTab.success_edit_text'));

            toggleEditModal();
        } catch (err) {
            const errorMessage = err?.error?.response?.data?.message?.[language] || t('specialitiesTab.errors.updateSpecialty_failed');
            toast.warn(errorMessage);
            setModalError(errorMessage);
        } finally {
            setIsUpdating(false);
        }
    };

    const handleEdit = (specialty) => {
        setEditSpecialty({
            name: specialty.name,
            color: specialty.color,
            specialtyId: specialty.specialtyId
        });
        setIsEditModalOpen(true);
    };

    const handleDelete = (specialty) => {
        Swal.fire({
            title: t('specialitiesTab.delete_confirm_title'),
            text: t('specialitiesTab.delete_confirm_text'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#d33',
            confirmButtonText: t('specialitiesTab.delete_confirm_button'),
            cancelButtonText: t('specialitiesTab.cancel_button'),
        }).then(async (result) => {
            if (!result.isConfirmed) return;

            try {
                await dispatch(
                    deleteSpeciality({ doctorId, specialtyId: specialty.specialtyId })
                ).unwrap();

                toast.success(t('specialitiesTab.success_delete_text'));
            } catch (err) {
                const errorMessage = err?.error?.response?.data?.message?.[language] || t('specialitiesTab.errors.deleteSpecialty_failed');
                toast.warn(errorMessage);
            }
        });
    };

    const handleSaveActiveSpecialty = async (e) => {
        e.preventDefault();
        if (!selectedSpecialty) {
            setModalError(t('specialitiesTab.errors.specialty_required'));
            return;
        }

        const result = await Swal.fire({
            title: t('specialitiesTab.confirm_save_title'),
            text: t('specialitiesTab.confirm_save_text'),
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#d33',
            confirmButtonText: t('specialitiesTab.yes_save'),
            cancelButtonText: t('specialitiesTab.cancel_button'),
        });

        if (!result.isConfirmed) return;

        setIsSaving(true);
        setModalError('');
        try {
            // Handle both string and number comparisons - selectedSpecialty is always a string from select
            const specialty = availableOptions.find(opt =>
                String(opt.value) === String(selectedSpecialty)
            );

            if (!specialty) {
                console.error('Specialty not found:', {
                    selectedSpecialty,
                    selectedSpecialtyType: typeof selectedSpecialty,
                    availableOptions: availableOptions.map(opt => ({ value: opt.value, valueType: typeof opt.value, name: opt.name }))
                });
                const errorMsg = t('specialitiesTab.errors.specialty_not_found');
                setModalError(errorMsg || 'Specialty not found. Please try again.');
                setIsSaving(false);
                return;
            }

            console.log('Found specialty:', specialty);
            // Convert to format expected by API (matching initialSelectedSpecialties structure)
            // Use the original ID (not the stringified value)
            const specialtyToAdd = {
                specialtyId: specialty.specialityId || specialty.value,
                name: specialty.name || specialty.label,
                color: selectedColor
            };
            await dispatch(
                specialitySetdoctor({
                    doctorId,
                    specialties: [specialtyToAdd]
                })
            ).unwrap();

            toast.success(t('specialitiesTab.success_save_text'));

            toggleActiveSpecialtyModal();
        } catch (err) {
            const errorMessage = err?.error?.response?.data?.message?.[language] || t('specialitiesTab.errors.saveSpecialty_failed');
            toast.warn(errorMessage);
            setModalError(errorMessage);
        } finally {
            setIsSaving(false);
        }
    };

    const handleAppointmentAddClick = () => {
        setAppointmentData({
            specialty: '',
            type: '',
            hours: 0,
            minutes: 0,
            price: '',
            visioAccepted: false,
            typeId: null,
        });
        setIsAppointmentModalOpen(true);
    };

    const handleCreateAppointment = async (e) => {
        e.preventDefault();
        setModalError('');

        // If both APIs failed, don't allow any submission (button should be disabled anyway)
        if (appointmentCheckVisioError) {
            return;
        }

        // If canEditOnlyMode is true, only allow updating appointment mode
        if (canEditOnlyMode) {
            const { specialty, visioAccepted, typeId } = appointmentData;

            const result = await Swal.fire({
                title: t('specialitiesTab.confirm_update_appointment_mode_title'),
                text: t('specialitiesTab.confirm_update_appointment_mode_text'),
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#4ABDBD',
                cancelButtonColor: '#d33',
                confirmButtonText: t('specialitiesTab.yes_update'),
                cancelButtonText: t('specialitiesTab.cancel_button'),
            });

            if (!result.isConfirmed) return;

            setIsCreatingAppointment(true);
            try {
                // Only send the virtual field with existing data
                const specialty_data = doctorSelected.find(s => s.specialtyId === specialty);
                const appointment = specialty_data?.appointmentTypes?.find(app => app.typeId === typeId);

                const payload = {
                    specialid: specialty,
                    doctorId,
                    val: {
                        type: appointment.type,
                        duration: appointment.duration,
                        price: appointment.price,
                        virtual: visioAccepted,
                        typeId
                    }
                };
                await dispatch(updateAppointmentSpeciality(payload)).unwrap();

                toast.success(t('specialitiesTab.success_update_appointment_mode_text'));

                toggleAppointmentModal();
            } catch (err) {
                console.log('err', err);
                const errorMessage = err?.error?.response?.data?.message?.[language] || t('specialitiesTab.errors.appointment_failed');
                toast.warn(errorMessage);
                setModalError(errorMessage);
            } finally {
                setIsCreatingAppointment(false);
            }
            return;
        }

        // Normal validation for full edit
        if (!validateAppointment()) return;

        const { specialty, type, hours, minutes, price, visioAccepted, typeId } = appointmentData;
        const duration = `${hours}:${minutes.toString().padStart(2, '0')}`;

        const result = await Swal.fire({
            title: typeId
                ? t('specialitiesTab.confirm_update_appointment_title')
                : t('specialitiesTab.confirm_create_appointment_title'),
            text: typeId
                ? t('specialitiesTab.confirm_update_appointment_text')
                : t('specialitiesTab.confirm_create_appointment_text'),
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#d33',
            confirmButtonText: typeId
                ? t('specialitiesTab.yes_update')
                : t('specialitiesTab.yes_create'),
            cancelButtonText: t('specialitiesTab.cancel_button'),
        });

        if (!result.isConfirmed) return;

        setIsCreatingAppointment(true);
        try {
            if (typeId) {
                const payload = {
                    specialid: specialty,
                    doctorId,
                    val: { type, duration, price, virtual: visioAccepted, typeId }
                };
                await dispatch(updateAppointmentSpeciality(payload)).unwrap();

                toast.success(t('specialitiesTab.success_update_appointment_text'));
            } else {
                const payload = {
                    specialid: specialty,
                    doctorId,
                    val: { type, duration, price, virtual: visioAccepted }
                };
                await dispatch(createAppointmentSpeciality(payload)).unwrap();

                toast.success(t('specialitiesTab.success_create_appointment_text'));
            }

            toggleAppointmentModal();
        } catch (err) {
            console.log('err', err);
            const errorMessage = err?.error?.response?.data?.message?.[language] || t('specialitiesTab.errors.appointment_failed');
            toast.warn(errorMessage);
            setModalError(errorMessage);
        } finally {
            setIsCreatingAppointment(false);
        }
    };

    const handleEditAppointment = async (specialtyId, typeId) => {
        const specialty = doctorSelected.find(s => s.specialtyId === specialtyId);
        const appointment = specialty?.appointmentTypes?.find(app => app.typeId === typeId);
        if (appointment) {
            const [hours, minutes] = appointment.duration.split(':').map(Number);
            setAppointmentData({
                specialty: specialtyId,
                type: appointment.type,
                hours,
                minutes,
                price: appointment.price,
                visioAccepted: appointment.virtual,
                typeId: appointment.typeId,
            });
            setIsAppointmentModalOpen(true);
            setAppointmentCheckError('');
            setAppointmentCheckVisioError('');
            setCanEditOnlyMode(false);

            // Check if visioAccepted is true or false
            if (appointment.virtual === false) {
                // If visio is NO (false), call only checkAppointmentByTypeId API
                try {
                    await dispatch(checkAppointmentByTypeId({
                        doctorId,
                        specialtyId,
                        typeId
                    })).unwrap();
                    // API success - enable all fields for full editing
                    setAppointmentCheckError('');
                    setAppointmentCheckVisioError('');
                    setCanEditOnlyMode(false);
                } catch (err) {
                    // API failed - disable all fields except appointment mode
                    const errorMessage = err?.error?.response?.data?.message?.[language] || t('specialitiesTab.errors.appointment_check_failed');
                    setAppointmentCheckError(errorMessage);
                    setCanEditOnlyMode(true);
                }
            } else {
                // If visio is YES (true), call both APIs in parallel
                try {
                    const [typeIdResult, visioResult] = await Promise.allSettled([
                        dispatch(checkAppointmentByTypeId({
                            doctorId,
                            specialtyId,
                            typeId
                        })).unwrap(),
                        dispatch(checkAppointmentByVisio({
                            doctorId,
                            specialtyId,
                            typeId
                        })).unwrap()
                    ]);

                    const typeIdSuccess = typeIdResult.status === 'fulfilled';
                    const visioSuccess = visioResult.status === 'fulfilled';

                    if (typeIdSuccess && visioSuccess) {
                        // ✅ Both APIs succeeded - enable all fields
                        setAppointmentCheckError('');
                        setAppointmentCheckVisioError('');
                        setCanEditOnlyMode(false);
                    } else if (!typeIdSuccess && !visioSuccess) {
                        // ❌❌ Both APIs failed - disable all fields and update button
                        const visioErrorMessage = visioResult.reason?.error?.response?.data?.message?.[language] || t('specialitiesTab.errors.appointment_check_failed');
                        setAppointmentCheckVisioError(visioErrorMessage);
                        setCanEditOnlyMode(false);
                    } else if (!typeIdSuccess && visioSuccess) {
                        // ❌✅ Only typeId API failed - allow editing appointment mode only
                        const errorMessage = typeIdResult.reason?.error?.response?.data?.message?.[language] || t('specialitiesTab.errors.appointment_check_failed');
                        setAppointmentCheckError(errorMessage);
                        setCanEditOnlyMode(true);
                    }
                    // If only visio fails but typeId succeeds (✅❌), all fields are enabled (no error state)
                } catch (err) {
                    console.error('Error checking appointment:', err);
                }
            }
        }
    };

    const handleDeleteAppointment = (specialtyId, typeId) => {
        Swal.fire({
            title: t('specialitiesTab.delete_appointment_confirm_title'),
            text: t('specialitiesTab.delete_appointment_confirm_text'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#d33',
            confirmButtonText: t('specialitiesTab.delete_confirm_button'),
            cancelButtonText: t('specialitiesTab.cancel_button'),
        }).then(async (result) => {
            if (!result.isConfirmed) return;

            try {
                await dispatch(
                    deleteAppointmentSpeciality({ doctorId, specialid: specialtyId, typeId })
                ).unwrap();

                toast.success(t('specialitiesTab.success_delete_appointment_text'));
            } catch (err) {
                const errorMessage = err?.error?.response?.data?.message?.[language] || t('specialitiesTab.errors.delete_appointment_failed');
                toast.warn(errorMessage);
            }
        });
    };

    const handleremove = (specialty) => {
        Swal.fire({
            title: t('specialitiesTab.delete_confirm_title'),
            text: t('specialitiesTab.delete_confirm_text'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#d33',
            confirmButtonText: t('specialitiesTab.delete_confirm_button'),
            cancelButtonText: t('specialitiesTab.cancel_button'),
        }).then(async (result) => {
            if (!result.isConfirmed) return;

            const isActive = doctorSelected?.some(
                (doc) => doc.name === specialty.name
            );

            if (isActive) {
                toast.warn(t('specialitiesTab.delete_error_active'));
                return;
            }

            try {
                await dispatch(
                    removeSpeciality({ doctorId, specialtyId: specialty.uniqueId })
                ).unwrap();

                toast.success(t('specialitiesTab.success_delete_text'));
            } catch (err) {
                const errorMessage = err?.error?.response?.data?.message?.[language] || t('specialitiesTab.errors.deleteSpecialty_failed');
                toast.warn(errorMessage);
            }
        });
    };

    // Create options from master specialties (filter out already selected)
    const availableOptions = (masterSpecialties || [])
        .filter(spec => {
            // Filter out already selected specialties and those without a valid ID
            // API returns specialtyId (with 'y'), check for that first
            const id = spec.specialtyId || spec.specialityId || spec.id;
            return id && !doctorSelected.some(sel => sel.name === spec.name);
        })
        .map(spec => {
            // API returns specialtyId (with 'y'), check for that first
            const id = spec.specialtyId || spec.specialityId || spec.id;
            return {
                value: String(id), // Ensure it's a string for select comparison
                label: spec.name,
                name: spec.name,
                specialityId: id
            };
        });

    const totalAppointments = doctorSelected.reduce((sum, s) => sum + (s.appointmentTypes?.length || 0), 0);

    console.log("doctorSelected", selectedDoctor);
    return (
        <div data-testid="specialities-tab-container" className="mt-4">
            {/* Active Specialities Section */}
            <div data-testid="active-specialities-header" className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                <h2 data-testid="active-specialities-title" className="text-lg sm:text-xl md:text-2xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
                    {t('specialitiesTab.active_specialities_heading')}
                </h2>
                <div className="flex gap-3">
                    {/* {specialties.length !== 0 && ( */}
                    <button
                        data-testid="modify-active-specialty-button"
                        onClick={toggleActiveSpecialtyModal}
                        className="group flex items-center gap-2 w-full sm:w-auto cursor-pointer px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold relative overflow-hidden text-sm"
                    >
                        <span data-testid="modify-active-specialty-text">{t('specialitiesTab.modify_button')}</span>
                    </button>
                    {/* )} */}
                </div>
            </div>
            <div className="flex flex-wrap gap-4 mb-10">
                {doctorSelected.length === 0 ? (
                    <div data-testid="no-active-specialties" className="flex flex-col items-center justify-center w-full min-h-[200px] px-4 sm:px-6 lg:px-8">
                        <img
                            data-testid="no-active-specialties-image"
                            src={empty3}
                            alt={t('specialitiesTab.no_active_specialties_found')}
                            className="w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 lg:w-52 lg:h-52 object-contain"
                        />
                        <p data-testid="no-active-specialties-text" className="text-gray-500 text-center text-sm sm:text-base md:text-lg">
                            {t('specialitiesTab.no_specialties_found')}
                        </p>
                    </div>
                ) : (
                    doctorSelected.map((specialty, index) => (
                        <div
                            key={index}
                            className="flex items-center justify-between gap-3 py-2 px-4 rounded-full shadow w-full sm:w-auto max-w-fit"
                            style={{ backgroundColor: specialty.color }}
                            data-testid={`active-specialty-item-${index}`}
                        >
                            <span className="text-white font-semibold text-base inline-flex items-center gap-2">
                                <SpecialtyIcon specialtyId={specialty.specialtyId} categoryId={specialty.categoryId} size={18} />
                                <MasterLabel type="masterSpecialty" id={specialty.specialtyId} field="name" fallback={specialty.name} />
                            </span>
                            <div className="flex items-center gap-2">
                                {/* <button
                                    onClick={() => handleEdit(specialty)}
                                    className="p-2 text-white focus:outline-none cursor-pointer bg-white/20 rounded-2xl shadow backdrop-blur-[0.3px]"
                                    data-testid={`edit-active-specialty-button-${index}`}
                                >
                                    <Edit size={18} />
                                </button> */}
                                <button
                                    onClick={() => handleDelete(specialty)}
                                    className="p-2 text-white focus:outline-none cursor-pointer bg-white/20 rounded-2xl shadow backdrop-blur-[0.3px]"
                                    data-testid={`delete-active-specialty-button-${index}`}
                                >
                                    <Trash2 size={18} />
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>
            <div className="border border-gray-300 h-[1px] mt-16 mb-10"></div>
            {/* Appointment Specialities Section */}
            <div data-testid="appointment-specialities-header" className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                <h2 data-testid="appointment-specialities-title" className="text-lg sm:text-xl md:text-2xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
                    {t('specialitiesTab.appointment_specialities_heading')}
                </h2>
                <button
                    data-testid="add-appointment-specialty-button"
                    onClick={handleAppointmentAddClick}
                    className="group flex items-center gap-2 w-full sm:w-auto cursor-pointer px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold relative overflow-hidden text-sm"
                >
                    <Plus data-testid="add-appointment-specialty-icon" className="w-5 h-5" />
                    <span data-testid="add-appointment-specialty-text">{t('specialitiesTab.add_appointment_speciality_button')}</span>
                </button>
            </div>
            {/* Table for larger screens, Cards for mobile */}
            <div className={`hidden sm:block overflow-x-auto rounded-md  ${totalAppointments === 0 ? '' : 'shadow'}`}>
                <div className="max-h-[400px] custom-scrollbargray overflow-y-auto">
                    {totalAppointments === 0 ? (
                        <div data-testid="no-appointments-empty-state" className="flex flex-col items-center justify-center min-h-[200px] px-4 sm:px-6 lg:px-8">
                            <img
                                data-testid="no-appointments-image"
                                src={empty2}
                                alt={t('specialitiesTab.no_appointments_found')}
                                className="w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 lg:w-52 lg:h-52 object-contain"
                            />
                            <p data-testid="no-appointments-text" className="text-gray-500 text-center text-sm sm:text-base md:text-lg">
                                {t('specialitiesTab.no_appointments_found')}
                            </p>
                        </div>
                    ) : (
                        <table data-testid="appointments-table" className="w-full border-collapse rounded-md overflow-hidden min-w-[640px] sm:min-w-full">
                            <thead>
                                <tr data-testid="table-header" className="bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white">
                                    <th data-testid="specialities-header" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('specialitiesTab.specialities_column')}</th>
                                    <th data-testid="type-header" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('specialitiesTab.type_column')}</th>
                                    <th data-testid="duration-header" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('specialitiesTab.duration_column')}</th>
                                    <th data-testid="visio-header" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('specialitiesTab.visio_accepted_column')}</th>
                                    <th data-testid="price-header" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('specialitiesTab.price_column')}</th>
                                    <th data-testid="action-header" className="p-3 text-center text-xs font-bold uppercase tracking-wider">{t('specialitiesTab.action_column')}</th>
                                </tr>
                            </thead>
                            <tbody data-testid="appointments-table-body">
                                {doctorSelected.map((specialty) =>
                                    (specialty.appointmentTypes || []).map((type, index) => (
                                        <tr key={type.typeId} data-testid={`appointment-row-${index}`} className="border-b border-gray-100/50 hover:bg-gradient-to-r hover:from-blue-50/80 hover:to-cyan-50/80 transition-all duration-300 group">
                                            <td data-testid={`appointment-specialty-${type.typeId}`} className="p-3">
                                                <span className="inline-flex items-center gap-1 px-3 py-1 text-xs font-bold text-white rounded-full" style={{ backgroundColor: specialty.color }}>
                                                    <SpecialtyIcon specialtyId={specialty.specialtyId} categoryId={specialty.categoryId} size={12} />
                                                    <MasterLabel type="masterSpecialty" id={specialty.specialtyId} field="name" fallback={specialty.name} />
                                                </span>
                                            </td>
                                            <td data-testid={`appointment-type-${type.typeId}`} className="p-3 text-xs font-semibold transition-colors duration-300" style={{ color: specialty.color }}>{type.type}</td>
                                            <td data-testid={`appointment-duration-${type.typeId}`} className="p-3 text-xs text-gray-600 group-hover:text-gray-700 transition-colors duration-300">{type.duration}</td>
                                            <td data-testid={`appointment-visio-${type.typeId}`} className="p-3 text-xs">
                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-gradient-to-br from-[#4ABDBD]/10 to-[#3A9B9B]/10 text-[#3AA8A8] border border-[#4ABDBD]/20">
                                                    {type.virtual ? t('specialitiesTab.visio_accepted_yes') : t('specialitiesTab.visio_accepted_no')}
                                                </span>
                                            </td>
                                            <td
                                                data-testid={`appointment-price-${type.typeId}`}
                                                className="p-3 text-xs text-gray-600 group-hover:text-gray-700 transition-colors duration-300"
                                            >
                                                {doctorCurrencySymbol(selectedDoctor)} {type.price}
                                            </td>
                                            <td data-testid={`appointment-actions-${type.typeId}`} className="p-3 text-xs flex justify-center gap-2">
                                                <button
                                                    data-testid={`edit-appointment-${type.typeId}`}
                                                    onClick={() => handleEditAppointment(specialty.specialtyId, type.typeId)}
                                                    className="group/btn text-[#4ABDBD] hover:text-white p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                                >
                                                    <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                    <Edit data-testid={`edit-icon-${type.typeId}`} className="h-4 w-4 relative z-10" />
                                                </button>
                                                <button
                                                    data-testid={`delete-appointment-${type.typeId}`}
                                                    onClick={() => handleDeleteAppointment(specialty.specialtyId, type.typeId)}
                                                    className="group/btn text-red-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-red-50 to-pink-50 hover:from-red-500 hover:to-pink-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                                >
                                                    <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                    <Trash2 data-testid={`delete-icon-${type.typeId}`} className="h-4 w-4 relative z-10" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Card layout for mobile screens */}
            <div className="block sm:hidden space-y-4">
                {totalAppointments > 0 ? (
                    doctorSelected.map((specialty) =>
                        (specialty.appointmentTypes || []).map((type, index) => (
                            <div
                                key={type.typeId}
                                data-testid={`appointment-card-${type.typeId}`}
                                className="p-4 bg-gradient-to-r bg-gray-50 rounded-xl shadow-md hover:shadow-lg transition-all duration-300"
                            >
                                <div className="flex justify-between items-center mb-2">
                                    <span data-testid={`appointment-serial-${type.typeId}`} className="text-xs font-bold text-[#4ABDBD]">
                                        {t('specialitiesTab.serial_no')}: {index + 1}
                                    </span>
                                    <div className="flex gap-2">
                                        <button
                                            data-testid={`edit-appointment-${type.typeId}`}
                                            onClick={() => handleEditAppointment(specialty.specialtyId, type.typeId)}
                                            className="group/btn text-[#4ABDBD] hover:text-white p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                        >
                                            <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                            <Edit data-testid={`edit-icon-${type.typeId}`} className="h-4 w-4 relative z-10" />
                                        </button>
                                        <button
                                            data-testid={`delete-appointment-${type.typeId}`}
                                            onClick={() => handleDeleteAppointment(specialty.specialtyId, type.typeId)}
                                            className="group/btn text-red-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-red-50 to-pink-50 hover:from-red-500 hover:to-pink-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                        >
                                            <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                            <Trash2 data-testid={`delete-icon-${type.typeId}`} className="h-4 w-4 relative z-10" />
                                        </button>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <div data-testid={`appointment-specialty-${type.typeId}`} className="text-sm">
                                        <span className="font-bold text-gray-800">{t('specialitiesTab.specialities_column')}:</span> <span className="inline-block px-3 py-1 text-xs font-bold text-white rounded-full" style={{ backgroundColor: specialty.color }}><MasterLabel type="masterSpecialty" id={specialty.specialtyId} field="name" fallback={specialty.name} /></span>
                                    </div>
                                    <div data-testid={`appointment-type-${type.typeId}`} className="text-xs text-gray-600">
                                        <span className="font-bold">{t('specialitiesTab.type_column')}:</span> <span className="font-semibold" style={{ color: specialty.color }}>{type.type}</span>
                                    </div>
                                    <div data-testid={`appointment-duration-${type.typeId}`} className="text-xs text-gray-600">
                                        <span className="font-bold">{t('specialitiesTab.duration_column')}:</span> {type.duration}
                                    </div>
                                    <div data-testid={`appointment-visio-${type.typeId}`} className="text-xs">
                                        <span className="font-bold">{t('specialitiesTab.visio_accepted_column')}:</span>
                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-gradient-to-br from-[#4ABDBD]/10 to-[#3A9B9B]/10 text-[#3AA8A8] border border-[#4ABDBD]/20 ml-1">
                                            {type.virtual ? t('specialitiesTab.visio_accepted_yes') : t('specialitiesTab.visio_accepted_no')}
                                        </span>
                                    </div>
                                    <div data-testid={`appointment-price-${type.typeId}`} className="text-xs text-gray-600">
                                        <span className="font-bold">{t('specialitiesTab.price_column')}:</span> {doctorCurrencySymbol(selectedDoctor)} {type.price}
                                    </div>
                                </div>
                            </div>
                        ))
                    )
                ) : (
                    <div data-testid="no-appointments-row" className="p-3 text-center text-gray-500">
                        {t('specialitiesTab.no_appointments_found')}
                    </div>
                )}
            </div>
            {/* Add Specialty Modal */}
            {isModalOpen && (
                <div
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                    role="dialog"
                    aria-labelledby="add-specialty-modal-title"
                    id="add-specialty-modal"
                    data-testid="add-specialty-modal"
                >
                    <div className="bg-white my-5 rounded-2xl shadow-2xl w-full max-w-md sm:max-w-lg md:max-w-2xl max-h-[80vh] sm:max-h-[90vh] flex flex-col">
                        {/* Header Section - Sticky */}
                        <div className="p-4 sm:p-6 sticky top-0 bg-gradient-to-r from-[#4ABDBD]/5 to-[#3AA8A8]/5 z-10 flex justify-between items-center border-b border-gray-200">
                            <h3
                                className="text-base sm:text-lg md:text-xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent"
                                id="add-specialty-modal-title"
                                data-testid="add-specialty-modal-title"
                            >
                                {t('specialitiesTab.add_speciality_modal_title')}
                            </h3>
                            <button
                                id="add-specialty-modal-close-button"
                                onClick={toggleModal}
                                aria-label={t('specialitiesTab.close_modal')}
                                className="group/btn text-gray-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-400 hover:to-gray-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                data-testid="add-specialty-modal-close-button"
                            >
                                <X className="w-5 h-5 sm:w-6 sm:h-6" />
                            </button>
                        </div>

                        {/* Scrollable Form Content */}
                        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 custom-scrollbar">
                            {modalError && (
                                <div
                                    className="bg-red-100 p-2 rounded text-red-700 mb-4"
                                    data-testid="modal-error-add-specialty"
                                >
                                    {modalError}
                                </div>
                            )}
                            <form id="add-specialty-form" onSubmit={handleAddSpecialty} data-testid="add-specialty-form">
                                <div className="grid grid-cols-1 gap-y-4">
                                    <div className="mb-4">
                                        <label
                                            className="block text-sm font-semibold text-gray-700 mb-2"
                                            htmlFor="new-specialty-name-input"
                                            id="new-specialty-name-label"
                                            data-testid="new-specialty-name-label"
                                        >
                                            {t('specialitiesTab.name_label')} <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            id="new-specialty-name-input"
                                            name="name"
                                            value={newSpecialty.name}
                                            onChange={handleInputChange}
                                            className={`w-full p-3 rounded-xl border text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${errors.newSpecialtyName ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-[#4ABDBD]'
                                                }`}
                                            placeholder={t('specialitiesTab.name_placeholder')}
                                            maxLength={20}
                                            required
                                            data-testid="new-specialty-name-input"
                                        />
                                        {errors.newSpecialtyName && (
                                            <p
                                                className="mt-2 text-xs text-red-500 font-medium"
                                                id="new-specialty-name-error"
                                                data-testid="new-specialty-name-error"
                                            >
                                                {errors.newSpecialtyName}
                                            </p>
                                        )}
                                        {charLimitReached.newSpecialtyName && <p className="mt-2 text-xs text-red-500 font-medium">{t('specialitiesTab.reached_text_limit', { limit: 20 })}</p>}
                                    </div>
                                    <div className="mb-4">
                                        <label
                                            className="block text-sm font-semibold text-gray-700 mb-2"
                                            htmlFor="new-specialty-color-input"
                                            id="new-specialty-color-label"
                                            data-testid="new-specialty-color-label"
                                        >
                                            {t('specialitiesTab.color_label')}
                                        </label>
                                        <input
                                            type="color"
                                            id="new-specialty-color-input"
                                            name="color"
                                            value={newSpecialty.color}
                                            onChange={handleInputChange}
                                            className="w-full h-10 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] cursor-pointer transition-all duration-300"
                                            data-testid="new-specialty-color-input"
                                        />
                                    </div>
                                </div>
                            </form>
                        </div>

                        {/* Footer Section - Sticky */}
                        <div className="sticky bottom-0 p-4 sm:p-6 border-t rounded-2xl border-gray-200 bg-gradient-to-r from-gray-50/50 to-blue-50/30 flex justify-end gap-4">
                            <button
                                type="button"
                                id="add-specialty-form-cancel-button"
                                onClick={toggleModal}
                                className="px-4 py-2 bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm hover:scale-105 cursor-pointer"
                                disabled={isAdding}
                                data-testid="add-specialty-modal-cancel-button"
                            >
                                {t('specialitiesTab.cancel_button')}
                            </button>
                            <button
                                type="submit"
                                id="add-specialty-form-submit-button"
                                form="add-specialty-form"
                                className={`group flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm ${isAdding || errors.newSpecialtyName ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105 cursor-pointer'
                                    }`}
                                disabled={isAdding || errors.newSpecialtyName}
                                data-testid="add-specialty-modal-create-button"
                            >
                                {isAdding && (
                                    <span data-testid="loading-spinner-add-specialty" className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>
                                )}
                                {isAdding ? t('specialitiesTab.creating_button') : t('specialitiesTab.create_button')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Edit Specialty Modal */}
            {isEditModalOpen && (
                <div
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                    role="dialog"
                    aria-labelledby="edit-specialty-modal-title"
                    id="edit-specialty-modal"
                    data-testid="edit-specialty-modal"
                >
                    <div className="bg-white my-5 rounded-2xl shadow-2xl w-full max-w-md sm:max-w-lg md:max-w-2xl max-h-[80vh] sm:max-h-[90vh] flex flex-col">
                        {/* Header Section - Sticky */}
                        <div className="p-4 sm:p-6 sticky top-0 bg-gradient-to-r from-[#4ABDBD]/5 to-[#3AA8A8]/5 z-10 flex justify-between items-center border-b border-gray-200">
                            <h3
                                className="text-base sm:text-lg md:text-xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent"
                                id="edit-specialty-modal-title"
                                data-testid="edit-specialty-modal-title"
                            >
                                {t('specialitiesTab.edit_speciality_modal_title')}
                            </h3>
                            <button
                                id="edit-specialty-modal-close-button"
                                onClick={toggleEditModal}
                                aria-label={t('specialitiesTab.close_modal')}
                                className="group/btn text-gray-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-400 hover:to-gray-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                data-testid="edit-specialty-modal-close-button"
                            >
                                <X className="w-5 h-5 sm:w-6 sm:h-6" />
                            </button>
                        </div>

                        {/* Scrollable Form Content */}
                        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 custom-scrollbar">
                            {modalError && (
                                <div
                                    className="bg-red-100 p-2 rounded text-red-700 mb-4"
                                    data-testid="modal-error-edit-specialty"
                                >
                                    {modalError}
                                </div>
                            )}
                            <form id="edit-specialty-form" onSubmit={handleEditSpecialty} data-testid="edit-specialty-form">
                                <div className="grid grid-cols-1 gap-y-4">
                                    <div className="mb-4">
                                        <label
                                            className="block text-sm font-semibold text-gray-700 mb-2"
                                            htmlFor="edit-specialty-name-input"
                                            id="edit-specialty-name-label"
                                            data-testid="edit-specialty-name-label"
                                        >
                                            {t('specialitiesTab.name_label')} <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            id="edit-specialty-name-input"
                                            name="name"
                                            value={editSpecialty.name}
                                            onChange={handleEditInputChange}
                                            className={`w-full p-3 rounded-xl border text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${errors.editSpecialtyName ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-[#4ABDBD]'
                                                }`}
                                            placeholder={t('specialitiesTab.name_placeholder')}
                                            maxLength={20}
                                            required
                                            data-testid="edit-specialty-name-input"
                                        />
                                        {errors.editSpecialtyName && (
                                            <p
                                                className="mt-2 text-xs text-red-500 font-medium"
                                                id="edit-specialty-name-error"
                                                data-testid="edit-specialty-name-error"
                                            >
                                                {errors.editSpecialtyName}
                                            </p>
                                        )}
                                        {charLimitReached.editSpecialtyName && <p className="mt-2 text-xs text-red-500 font-medium">{t('specialitiesTab.reached_text_limit', { limit: 20 })}</p>}
                                    </div>
                                    <div className="mb-4">
                                        <label
                                            className="block text-sm font-semibold text-gray-700 mb-2"
                                            htmlFor="edit-specialty-color-input"
                                            id="edit-specialty-color-label"
                                            data-testid="edit-specialty-color-label"
                                        >
                                            {t('specialitiesTab.color_label')}
                                        </label>
                                        <input
                                            type="color"
                                            id="edit-specialty-color-input"
                                            name="color"
                                            value={editSpecialty.color}
                                            onChange={handleEditInputChange}
                                            className="w-full h-10 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] cursor-pointer transition-all duration-300"
                                            data-testid="edit-specialty-color-input"
                                        />
                                    </div>
                                </div>
                            </form>
                        </div>

                        {/* Footer Section - Sticky */}
                        <div className="sticky bottom-0 p-4 sm:p-6 border-t rounded-2xl border-gray-200 bg-gradient-to-r from-gray-50/50 to-blue-50/30 flex justify-end gap-4">
                            <button
                                type="button"
                                id="edit-specialty-form-cancel-button"
                                onClick={toggleEditModal}
                                className="px-4 py-2 bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm hover:scale-105 cursor-pointer"
                                disabled={isUpdating}
                                data-testid="edit-specialty-modal-cancel-button"
                            >
                                {t('specialitiesTab.cancel_button')}
                            </button>
                            <button
                                type="submit"
                                id="edit-specialty-form-submit-button"
                                form="edit-specialty-form"
                                className={`group flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm ${isUpdating || errors.editSpecialtyName ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105 cursor-pointer'
                                    }`}
                                disabled={isUpdating || errors.editSpecialtyName}
                                data-testid="edit-specialty-modal-update-button"
                            >
                                {isUpdating && (
                                    <span data-testid="loading-spinner-edit-specialty" className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>
                                )}
                                {isUpdating ? t('specialitiesTab.updating_button') : t('specialitiesTab.update_button')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Active Specialty Modal */}
            {isActiveSpecialtyModalOpen && (
                <div
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                    role="dialog"
                    aria-labelledby="active-specialty-modal-title"
                    id="active-specialty-modal"
                    data-testid="active-specialty-modal"
                >
                    <div className="bg-white my-5 rounded-2xl shadow-2xl w-full max-w-md sm:max-w-lg md:max-w-2xl min-h-[550px] max-h-[85vh] sm:max-h-[90vh] flex flex-col">
                        {/* Header Section - Sticky */}
                        <div className="p-4 sm:p-6 sticky top-0 bg-gradient-to-r from-[#4ABDBD]/5 to-[#3AA8A8]/5 z-10 flex justify-between items-center border-b border-gray-200">
                            <h3
                                className="text-base sm:text-lg md:text-xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent"
                                id="active-specialty-modal-title"
                                data-testid="active-specialty-modal-title"
                            >
                                {t('specialitiesTab.make_active_speciality_modal_title')}
                            </h3>
                            <button
                                id="active-specialty-modal-close-button"
                                onClick={toggleActiveSpecialtyModal}
                                aria-label={t('specialitiesTab.close_modal')}
                                className="group/btn text-gray-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-400 hover:to-gray-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                data-testid="active-specialty-modal-close-button"
                            >
                                <X className="w-5 h-5 sm:w-6 sm:h-6" />
                            </button>
                        </div>

                        {/* Scrollable Form Content */}
                        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 custom-scrollbar">
                            {modalError && (
                                <div
                                    className="bg-red-100 p-2 rounded text-red-700 mb-4"
                                    data-testid="modal-error-active-specialty"
                                >
                                    {modalError}
                                </div>
                            )}
                            <form id="active-specialty-form" onSubmit={handleSaveActiveSpecialty} data-testid="active-specialty-form">
                                <div className="grid grid-cols-1 gap-y-4">
                                    {/* Category Filter */}
                                    <div className="mb-2">
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            {t('specialitiesTab.category_filter_label', 'Select Category')}
                                        </label>
                                        <div ref={categoryFilterDropdownRef} className="relative w-full">
                                            <button
                                                type="button"
                                                onClick={() => !spCategoryListLoading && setIsCategoryFilterDropdownOpen(!isCategoryFilterDropdownOpen)}
                                                disabled={spCategoryListLoading}
                                                className={`w-full p-3 border rounded-xl flex justify-between items-center font-medium transition text-sm
                                                ${spCategoryListLoading ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'text-gray-700 bg-white border-gray-300 hover:border-[#4ABDBD] cursor-pointer focus:ring-2 focus:ring-[#4ABDBD] focus:outline-none'}`}
                                                data-testid="doctor-specialities-category-filter-toggle"
                                            >
                                                <span className="truncate w-[85%] text-left flex items-center gap-2">
                                                    {selectedCategoryFilter && <SpecialtyIcon categoryId={selectedCategoryFilter.spcategoryId} size={16} className="text-[#4ABDBD]" />}
                                                    <span className="truncate min-w-0">
                                                        {selectedCategoryFilter
                                                            ? <MasterLabel type="masterSpecialtyCategory" id={selectedCategoryFilter.spcategoryId} field="spcategoryName" fallback={selectedCategoryFilter.spcategoryName} />
                                                            : t('specialitiesTab.select_category', 'Select Category')}
                                                    </span>
                                                </span>
                                                <span className="ml-2 flex items-center">
                                                    {spCategoryListLoading ? (
                                                        <span className="h-4 w-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></span>
                                                    ) : isCategoryFilterDropdownOpen ? (
                                                        <ChevronUp size={18} className="text-gray-500" />
                                                    ) : (
                                                        <ChevronDown size={18} className="text-gray-500" />
                                                    )}
                                                </span>
                                            </button>

                                            {isCategoryFilterDropdownOpen && (
                                                <div className="absolute left-0 mt-2 w-full z-[60] bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
                                                    {/* Search */}
                                                    <div className="p-2 border-b border-gray-100 bg-gray-50/50">
                                                        <div className="relative">
                                                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4ABDBD]" size={16} />
                                                            <input
                                                                ref={categorySearchInputRef}
                                                                type="text"
                                                                value={categorySearchQuery}
                                                                onChange={(e) => setCategorySearchQuery(e.target.value)}
                                                                placeholder={t('specialitiesTab.search_category', 'Search category...')}
                                                                className="w-full pl-10 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] focus:border-transparent bg-white transition-all"
                                                                autoFocus
                                                                data-testid="doctor-specialities-category-search-input"
                                                            />
                                                        </div>
                                                    </div>
                                                    <ul className="max-h-56 overflow-y-auto custom-scrollbar py-1">
                                                        {filteredCategoryList.length === 0 ? (
                                                            <li className="px-4 py-3 text-gray-500 text-center text-sm">
                                                                {t('specialitiesTab.no_categories_found', 'No categories found')}
                                                            </li>
                                                        ) : (
                                                            filteredCategoryList.map((cat, idx) => (
                                                                <CategoryRow
                                                                    key={cat.spcategoryId || idx}
                                                                    cat={cat}
                                                                    onClick={() => handleCategoryFilterSelect(cat)}
                                                                />
                                                            ))
                                                        )}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Specialty Select */}
                                    <div className="mb-4">
                                        <label
                                            className="block text-sm font-semibold text-gray-700 mb-2"
                                            htmlFor="active-specialty-select"
                                            id="active-specialty-label"
                                            data-testid="active-specialty-label"
                                        >
                                            {t('specialitiesTab.specialty_select_label')}
                                        </label>
                                        <div ref={specialtyDropdownRef} className="relative w-full">
                                            <button
                                                type="button"
                                                id="active-specialty-select"
                                                onClick={() => !specialtyLoading && selectedCategoryFilter && setIsSpecialtyDropdownOpen(!isSpecialtyDropdownOpen)}
                                                disabled={specialtyLoading || !selectedCategoryFilter}
                                                className={`w-full p-3 border rounded-xl flex justify-between items-center font-medium transition text-sm
                                                ${(specialtyLoading || !selectedCategoryFilter) ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'text-gray-700 bg-white border-gray-300 hover:border-[#4ABDBD] cursor-pointer focus:ring-2 focus:ring-[#4ABDBD] focus:outline-none'}`}
                                                data-testid="active-specialty-select"
                                            >
                                                <span className="truncate w-[85%] text-left">
                                                    {!selectedCategoryFilter ? (
                                                        t('specialitiesTab.select_category_first', 'Select category first')
                                                    ) : selectedSpecialty ? (
                                                        <SelectedSpecialtyText
                                                            specialtyId={selectedSpecialty}
                                                            fallback={
                                                                filteredAvailableOptions.find(opt => opt.value === selectedSpecialty)?.label
                                                                || masterSpecialties.find(s => String(s.specialtyId || s.specialityId || s.id) === selectedSpecialty)?.name
                                                                || t('specialitiesTab.specialty_select_placeholder')
                                                            }
                                                        />
                                                    ) : (
                                                        t('specialitiesTab.specialty_select_placeholder')
                                                    )}
                                                </span>
                                                <span className="ml-2 flex items-center">
                                                    {specialtyLoading ? (
                                                        <span className="h-4 w-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></span>
                                                    ) : isSpecialtyDropdownOpen ? (
                                                        <ChevronUp size={18} className="text-gray-500" />
                                                    ) : (
                                                        <ChevronDown size={18} className="text-gray-500" />
                                                    )}
                                                </span>
                                            </button>

                                            {isSpecialtyDropdownOpen && (
                                                <div className="absolute left-0 mt-2 w-full z-[60] bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden animate-in fade-in zoom-in duration-200">
                                                    <div className="p-2 border-b border-gray-100 bg-gray-50/50">
                                                        <div className="relative">
                                                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4ABDBD]" size={16} />
                                                            <input
                                                                type="text"
                                                                value={specialtySearchQuery}
                                                                onChange={handleSpecialtySearchChange}
                                                                placeholder={t('specialitiesTab.search_specialty_placeholder') !== 'specialitiesTab.search_specialty_placeholder' ? t('specialitiesTab.search_specialty_placeholder') : 'Search specialty...'}
                                                                className="w-full pl-10 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] focus:border-transparent bg-white transition-all"
                                                                autoFocus
                                                                data-testid="doctor-specialities-specialty-search-input"
                                                            />
                                                        </div>
                                                    </div>

                                                    <ul className="max-h-56 overflow-y-auto custom-scrollbar py-1">
                                                        {filteredAvailableOptions.length === 0 ? (
                                                            <li className="px-4 py-6 text-gray-500 text-center text-sm flex flex-col items-center gap-2">
                                                                <div className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center">
                                                                    <Search size={18} className="text-gray-300" />
                                                                </div>
                                                                {t('specialitiesTab.no_specialties_found') !== 'specialitiesTab.no_specialties_found' ? t('specialitiesTab.no_specialties_found') : 'No specialties found'}
                                                            </li>
                                                        ) : (
                                                            filteredAvailableOptions.map((option, index) => (
                                                                <PickerOptionRow
                                                                    key={option.specialityId || option.value || `option-${index}`}
                                                                    option={option}
                                                                    onClick={() => handleSpecialtyItemSelect(option)}
                                                                />
                                                            ))
                                                        )}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="mb-4">
                                        <label
                                            className="block text-sm font-semibold text-gray-700 mb-2"
                                            htmlFor="active-specialty-color-input"
                                            id="active-specialty-color-label"
                                            data-testid="active-specialty-color-label"
                                        >
                                            {t('specialitiesTab.color_label')}
                                        </label>
                                        <input
                                            type="color"
                                            id="active-specialty-color-input"
                                            name="color"
                                            value={selectedColor}
                                            onChange={handleColorChange}
                                            className="w-full h-10 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] cursor-pointer transition-all duration-300"
                                            data-testid="active-specialty-color-input"
                                        />
                                    </div>
                                </div>
                            </form>
                        </div>

                        {/* Footer Section - Sticky */}
                        <div className="sticky bottom-0 p-4 sm:p-6 border-t rounded-2xl border-gray-200 bg-gradient-to-r from-gray-50/50 to-blue-50/30 flex justify-end gap-4">
                            <button
                                type="button"
                                id="active-specialty-form-cancel-button"
                                onClick={toggleActiveSpecialtyModal}
                                className="px-4 py-2 bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm hover:scale-105 cursor-pointer"
                                disabled={isSaving}
                                data-testid="active-specialty-modal-cancel-button"
                            >
                                {t('specialitiesTab.cancel_button')}
                            </button>
                            <button
                                type="submit"
                                id="active-specialty-form-submit-button"
                                form="active-specialty-form"
                                className={`group flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm ${isSaving || !selectedSpecialty ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105 cursor-pointer'
                                    }`}
                                disabled={isSaving || !selectedSpecialty}
                                data-testid="active-specialty-modal-add-button"
                            >
                                {isSaving && (
                                    <span data-testid="loading-spinner-active-specialty" className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>
                                )}
                                {isSaving ? t('specialitiesTab.saving_button') : t('specialitiesTab.add_button')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Appointment Specialty Modal */}
            {isAppointmentModalOpen && (
                <div
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                    role="dialog"
                    aria-labelledby="appointment-modal-title"
                    id="appointment-modal"
                    data-testid="appointment-modal"
                >
                    <div className="bg-white my-5 rounded-2xl shadow-2xl w-full max-w-md sm:max-w-lg md:max-w-2xl min-h-[600px] max-h-[85vh] sm:max-h-[90vh] flex flex-col">
                        {/* Header Section - Sticky */}
                        <div className="p-4 sm:p-6 sticky top-0 bg-gradient-to-r from-[#4ABDBD]/5 to-[#3AA8A8]/5 z-10 flex justify-between items-center border-b border-gray-200">
                            <h3
                                className="text-base sm:text-lg md:text-xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent"
                                id="appointment-modal-title"
                                data-testid="appointment-modal-title"
                            >
                                {appointmentData.typeId ? t('specialitiesTab.edit_appointment_speciality_modal_title') : t('specialitiesTab.add_appointment_speciality_modal_title')}
                            </h3>
                            <button
                                id="appointment-modal-close-button"
                                onClick={toggleAppointmentModal}
                                aria-label={t('specialitiesTab.close_modal')}
                                className="group/btn text-gray-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-400 hover:to-gray-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                data-testid="appointment-modal-close-button"
                            >
                                <X className="w-5 h-5 sm:w-6 sm:h-6" />
                            </button>
                        </div>

                        {/* Scrollable Form Content */}
                        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 custom-scrollbar">
                            {(checkAppointmentLoading || checkAppointmentVisioLoading) && (
                                <div className="flex flex-col items-center justify-center py-8">
                                    <div className="border-4 border-[#4ABDBD] border-t-transparent rounded-full w-12 h-12 animate-spin mb-4"></div>
                                    <p className="text-gray-600 text-sm">{t('specialitiesTab.checking_appointment')}</p>
                                </div>
                            )}
                            {appointmentCheckVisioError && (
                                <div
                                    className="bg-red-100 p-3 rounded-xl text-red-700 mb-4 border border-red-300"
                                    data-testid="appointment-check-visio-error"
                                >
                                    <p className="font-semibold text-sm">{appointmentCheckVisioError}</p>
                                    <p className="text-xs mt-1">{t('specialitiesTab.all_fields_disabled')}</p>
                                </div>
                            )}
                            {appointmentCheckError && canEditOnlyMode && (
                                <div
                                    className="bg-red-100 p-3 rounded-xl text-red-700 mb-4 border border-red-300"
                                    data-testid="appointment-check-error"
                                >
                                    <p className="font-semibold text-sm">{appointmentCheckError}</p>
                                    <p className="text-xs mt-1">{t('specialitiesTab.only_appointment_mode_editable')}</p>
                                </div>
                            )}
                            {modalError && (
                                <div
                                    className="bg-red-100 p-2 rounded text-red-700 mb-4"
                                    data-testid="modal-error-appointment"
                                >
                                    {modalError}
                                </div>
                            )}
                            <form id="appointment-form" onSubmit={handleCreateAppointment} data-testid="appointment-form" className="space-y-4">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-4">
                                    <div className="mb-4">
                                        <label
                                            className="block text-sm font-semibold text-gray-700 mb-2"
                                            htmlFor="appointment-specialty-select"
                                            id="appointment-specialty-label"
                                            data-testid="appointment-specialty-label"
                                        >
                                            {t('specialitiesTab.specialty_select_label')} <span className="text-red-500">*</span>
                                        </label>
                                        <div ref={appointmentSpecialtyDropdownRef} className="relative w-full">
                                            <button
                                                type="button"
                                                id="appointment-specialty-select"
                                                disabled={appointmentData.typeId || checkAppointmentLoading || checkAppointmentVisioLoading || appointmentCheckError || appointmentCheckVisioError}
                                                onClick={() => setIsAppointmentSpecialtyDropdownOpen(!isAppointmentSpecialtyDropdownOpen)}
                                                className={`w-full p-3 border rounded-xl flex justify-between items-center font-medium transition text-sm
                                                ${errors.appointmentSpecialty ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-[#4ABDBD]'}
                                                ${appointmentData.typeId || checkAppointmentLoading || checkAppointmentVisioLoading || appointmentCheckError || appointmentCheckVisioError ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white cursor-pointer focus:ring-2 focus:ring-[#4ABDBD] focus:outline-none'}`}
                                                data-testid="appointment-specialty-select"
                                            >
                                                <span className="truncate w-[85%] text-left">
                                                    {appointmentData.specialty ? (
                                                        <SelectedSpecialtyText
                                                            specialtyId={appointmentData.specialty}
                                                            fallback={
                                                                doctorSelected.find(s => s.specialtyId === appointmentData.specialty)?.name
                                                                || t('specialitiesTab.specialty_select_placeholder')
                                                            }
                                                        />
                                                    ) : (
                                                        t('specialitiesTab.specialty_select_placeholder')
                                                    )}
                                                </span>
                                                <span className="ml-2 flex items-center">
                                                    {checkAppointmentLoading || checkAppointmentVisioLoading ? (
                                                        <span className="h-4 w-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></span>
                                                    ) : isAppointmentSpecialtyDropdownOpen ? (
                                                        <ChevronUp size={18} className="text-gray-500" />
                                                    ) : (
                                                        <ChevronDown size={18} className="text-gray-500" />
                                                    )}
                                                </span>
                                            </button>

                                            {isAppointmentSpecialtyDropdownOpen && (
                                                <div className="absolute left-0 mt-2 w-full z-[60] bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden animate-in fade-in zoom-in duration-200">
                                                    <div className="p-2 border-b border-gray-100 bg-gray-50/50">
                                                        <div className="relative">
                                                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4ABDBD]" size={16} />
                                                            <input
                                                                type="text"
                                                                value={appointmentSpecialtySearchQuery}
                                                                onChange={handleAppointmentSpecialtySearchChange}
                                                                placeholder={t('specialitiesTab.search_specialty_placeholder') !== 'specialitiesTab.search_specialty_placeholder' ? t('specialitiesTab.search_specialty_placeholder') : 'Search specialty...'}
                                                                className="w-full pl-10 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] focus:border-transparent bg-white transition-all"
                                                                autoFocus
                                                                data-testid="doctor-specialities-appointment-specialty-search-input"
                                                            />
                                                        </div>
                                                    </div>

                                                    <ul className="max-h-56 overflow-y-auto custom-scrollbar py-1">
                                                        {filteredDoctorSelected.length === 0 ? (
                                                            <li className="px-4 py-6 text-gray-500 text-center text-sm flex flex-col items-center gap-2">
                                                                <div className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center">
                                                                    <Search size={18} className="text-gray-300" />
                                                                </div>
                                                                {t('specialitiesTab.no_specialties_found') !== 'specialitiesTab.no_specialties_found' ? t('specialitiesTab.no_specialties_found') : 'No specialties found'}
                                                            </li>
                                                        ) : (
                                                            filteredDoctorSelected.map((specialty) => (
                                                                <li
                                                                    key={specialty.specialtyId}
                                                                    onClick={() => handleAppointmentSpecialtyItemSelect(specialty)}
                                                                    className="px-4 py-2.5 transition text-sm cursor-pointer hover:bg-gradient-to-r hover:from-[#4ABDBD] hover:to-[#3AA8A8] hover:text-white border-b border-gray-50 last:border-0 font-medium text-gray-700"
                                                                >
                                                                    <MasterLabel type="masterSpecialty" id={specialty.specialtyId} field="name" fallback={specialty.name} />
                                                                </li>
                                                            ))
                                                        )}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                        {errors.appointmentSpecialty && (
                                            <p
                                                className="mt-2 text-xs text-red-500 font-medium"
                                                id="appointment-specialty-error"
                                                data-testid="appointment-specialty-error"
                                            >
                                                {errors.appointmentSpecialty}
                                            </p>
                                        )}
                                    </div>
                                    <div className="mb-4">
                                        <label
                                            className="block text-sm font-semibold text-gray-700 mb-2"
                                            htmlFor="appointment-type-input"
                                            id="appointment-type-label"
                                            data-testid="appointment-type-label"
                                        >
                                            {t('specialitiesTab.type_input_label')} <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            id="appointment-type-input"
                                            name="type"
                                            value={appointmentData.type}
                                            onChange={handleAppointmentInputChange}
                                            disabled={checkAppointmentLoading || checkAppointmentVisioLoading || appointmentCheckError || appointmentCheckVisioError}
                                            className={`w-full p-3 rounded-xl border text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${errors.appointmentType ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-[#4ABDBD]'
                                                } ${checkAppointmentLoading || checkAppointmentVisioLoading || appointmentCheckError || appointmentCheckVisioError ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''}`}
                                            placeholder={t('specialitiesTab.type_input_placeholder')}
                                            maxLength={20}
                                            required
                                            data-testid="appointment-type-input"
                                        />
                                        {errors.appointmentType && (
                                            <p
                                                className="mt-2 text-xs text-red-500 font-medium"
                                                id="appointment-type-error"
                                                data-testid="appointment-type-error"
                                            >
                                                {errors.appointmentType}
                                            </p>
                                        )}
                                        {charLimitReached.appointmentType && <p className="mt-2 text-xs text-red-500 font-medium">{t('specialitiesTab.reached_text_limit', { limit: 20 })}</p>}
                                    </div>
                                    <div className="mb-4 sm:col-span-2">
                                        <label
                                            className="block text-sm font-semibold text-gray-700 mb-2"
                                            id="appointment-duration-label"
                                            data-testid="appointment-duration-label"
                                        >
                                            {t('specialitiesTab.duration_label')} <span className="text-red-500">*</span>
                                        </label>
                                        <div className="flex flex-col sm:flex-row gap-4 mb-2">
                                            <div className="w-full sm:w-1/2">
                                                <label className="block text-xs font-medium text-gray-500">{t('specialitiesTab.hours_label')}</label>
                                                <input
                                                    type="range"
                                                    id="appointment-hours-range"
                                                    name="hours"
                                                    min="0"
                                                    max="3"
                                                    value={appointmentData.hours}
                                                    onChange={handleAppointmentInputChange}
                                                    disabled={checkAppointmentLoading || checkAppointmentVisioLoading || appointmentCheckError || appointmentCheckVisioError}
                                                    className={`w-full h-2 rounded-lg appearance-none [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-gray-600 [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-gray-600 [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:cursor-pointer ${checkAppointmentLoading || checkAppointmentVisioLoading || appointmentCheckError || appointmentCheckVisioError ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                                                    style={{ background: `linear-gradient(to right, #4ABDBD 0%, #4ABDBD ${(Number(appointmentData.hours) / 3) * 100}%, #E5E7EB ${(Number(appointmentData.hours) / 3) * 100}%, #E5E7EB 100%)` }}
                                                    data-testid="appointment-hours-range"
                                                />
                                                <div className="flex justify-between text-xs text-gray-600">
                                                    <span>0</span><span>1</span><span>2</span><span>3</span>
                                                </div>
                                            </div>
                                            <div className="w-full sm:w-1/2">
                                                <label className="block text-xs font-medium text-gray-500">{t('specialitiesTab.minutes_label')}</label>
                                                <input
                                                    type="range"
                                                    id="appointment-minutes-range"
                                                    name="minutes"
                                                    min="0"
                                                    max="45"
                                                    step="15"
                                                    value={appointmentData.minutes}
                                                    onChange={handleAppointmentInputChange}
                                                    disabled={checkAppointmentLoading || checkAppointmentVisioLoading || appointmentCheckError || appointmentCheckVisioError}
                                                    className={`w-full h-2 rounded-lg appearance-none [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-gray-600 [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-gray-600 [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:cursor-pointer ${checkAppointmentLoading || checkAppointmentVisioLoading || appointmentCheckError || appointmentCheckVisioError ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                                                    style={{ background: `linear-gradient(to right, #4ABDBD 0%, #4ABDBD ${(Number(appointmentData.minutes) / 45) * 100}%, #E5E7EB ${(Number(appointmentData.minutes) / 45) * 100}%, #E5E7EB 100%)` }}
                                                    data-testid="appointment-minutes-range"
                                                />
                                                <div className="flex justify-between text-xs text-gray-600">
                                                    <span>0</span><span>15</span><span>30</span><span>45</span>
                                                </div>
                                            </div>
                                        </div>
                                        {errors.appointmentDuration && (
                                            <p
                                                className="mt-2 text-xs text-red-500 font-medium"
                                                id="appointment-duration-error"
                                                data-testid="appointment-duration-error"
                                            >
                                                {errors.appointmentDuration}
                                            </p>
                                        )}
                                    </div>
                                    <div className="mb-4">
                                        <label
                                            className="block text-sm font-semibold text-gray-700 mb-2"
                                            htmlFor="appointment-price-input"
                                            id="appointment-price-label"
                                            data-testid="appointment-price-label"
                                        >
                                            {t('specialitiesTab.price_label')} <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            id="appointment-price-input"
                                            name="price"
                                            value={appointmentData.price}
                                            onChange={handleAppointmentInputChange}
                                            onKeyDown={(e) => { if (e.key === '.' || e.key === ',') e.preventDefault(); }}
                                            disabled={checkAppointmentLoading || checkAppointmentVisioLoading || appointmentCheckError || appointmentCheckVisioError}
                                            className={`w-full p-3 rounded-xl border text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${errors.appointmentPrice ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-[#4ABDBD]'
                                                } ${checkAppointmentLoading || checkAppointmentVisioLoading || appointmentCheckError || appointmentCheckVisioError ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''}`}
                                            placeholder={t('specialitiesTab.price_placeholder')}
                                            required
                                            data-testid="appointment-price-input"
                                        />
                                        {errors.appointmentPrice && (
                                            <p
                                                className="mt-2 text-xs text-red-500 font-medium"
                                                id="appointment-price-error"
                                                data-testid="appointment-price-error"
                                            >
                                                {errors.appointmentPrice}
                                            </p>
                                        )}
                                    </div>
                                    <div className="mb-4">
                                        <label
                                            className="block text-sm font-semibold text-gray-700 mb-2"
                                            htmlFor="appointment-visio-checkbox"
                                            id="appointment-visio-label"
                                            data-testid="appointment-visio-label"
                                        >
                                            {t('specialitiesTab.visio_accepted_label')}
                                        </label>
                                        <div className="flex items-center gap-2">
                                            <span className={`text-sm font-medium ${checkAppointmentLoading || checkAppointmentVisioLoading || appointmentCheckVisioError ? 'text-gray-400' : 'text-gray-900'}`}>{t('specialitiesTab.visio_accepted_no')}</span>
                                            <label title={!videoAllowed ? SECURED_VIDEO_MSG : ''} className={`relative inline-flex items-center ${checkAppointmentLoading || checkAppointmentVisioLoading || appointmentCheckVisioError || !videoAllowed ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                                                <input
                                                    type="checkbox"
                                                    id="appointment-visio-checkbox"
                                                    name="visioAccepted"
                                                    checked={appointmentData.visioAccepted}
                                                    onChange={handleAppointmentInputChange}
                                                    disabled={checkAppointmentLoading || checkAppointmentVisioLoading || appointmentCheckVisioError || !videoAllowed}
                                                    className="sr-only peer"
                                                    data-testid="appointment-visio-checkbox"
                                                />
                                                <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#4ABDBD] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-[#4ABDBD] peer-checked:to-[#3AA8A8] ${checkAppointmentLoading || checkAppointmentVisioLoading || appointmentCheckVisioError || !videoAllowed ? 'opacity-50' : ''}`}></div>
                                            </label>
                                            <span className={`ml-2 text-sm font-medium ${checkAppointmentLoading || checkAppointmentVisioLoading || appointmentCheckVisioError ? 'text-gray-400' : 'text-gray-900'}`}>{t('specialitiesTab.visio_accepted_yes')}</span>
                                        </div>
                                        {!videoAllowed && (
                                            <p className="text-xs text-amber-600 mt-1.5">{SECURED_VIDEO_MSG}</p>
                                        )}
                                    </div>
                                </div>
                            </form>
                        </div>

                        {/* Footer Section - Sticky */}
                        <div className="sticky bottom-0 p-4 sm:p-6 border-t rounded-2xl border-gray-200 bg-gradient-to-r from-gray-50/50 to-blue-50/30 flex justify-end gap-4">
                            <button
                                type="button"
                                id="appointment-form-cancel-button"
                                onClick={toggleAppointmentModal}
                                className="px-4 py-2 bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm hover:scale-105 cursor-pointer"
                                disabled={isCreatingAppointment || checkAppointmentLoading || checkAppointmentVisioLoading}
                                data-testid="appointment-modal-cancel-button"
                            >
                                {t('specialitiesTab.cancel_button')}
                            </button>
                            <button
                                type="submit"
                                id="appointment-form-submit-button"
                                form="appointment-form"
                                className={`group flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm ${isCreatingAppointment || checkAppointmentLoading || checkAppointmentVisioLoading || appointmentCheckVisioError || errors.appointmentSpecialty || errors.appointmentType || errors.appointmentPrice || errors.appointmentDuration ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105 cursor-pointer'
                                    }`}
                                disabled={isCreatingAppointment || checkAppointmentLoading || checkAppointmentVisioLoading || appointmentCheckVisioError || errors.appointmentSpecialty || errors.appointmentType || errors.appointmentPrice || errors.appointmentDuration}
                                data-testid="appointment-modal-submit-button"
                            >
                                {(isCreatingAppointment || checkAppointmentLoading || checkAppointmentVisioLoading) && (
                                    <span data-testid="loading-spinner-appointment" className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>
                                )}
                                {checkAppointmentLoading || checkAppointmentVisioLoading
                                    ? t('specialitiesTab.checking_button')
                                    : isCreatingAppointment
                                        ? (appointmentData.typeId ? t('specialitiesTab.saving_button') : t('specialitiesTab.creating_button'))
                                        : (appointmentData.typeId ? t('specialitiesTab.save_button') : t('specialitiesTab.create_button'))}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default SpecialitiesTab;